import React from 'react';
import { Search, X } from 'lucide-react';

export type SearchInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: string;
  onChange: (value: string) => void;
};

/**
 * The one search box: 36px, a search icon, and a way to clear it. There were 26, from 32 to 40px.
 *
 * The placeholder passes through untouched and the input stays a plain text box: page tests find
 * these inputs by placeholder and by role, and `type="search"` would change the role they ask for.
 * The width is the caller's, through `className` (default `w-64`).
 */
const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  placeholder = 'Search',
  className = 'w-64',
  'aria-label': ariaLabel,
  ...rest
}) => (
  <div className={`relative ${className}`}>
    <Search
      aria-hidden="true"
      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
    />
    <input
      {...rest}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={ariaLabel || placeholder}
      className="h-9 w-full rounded-md border border-slate-200 bg-white pl-9 pr-8 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
    />
    {value ? (
      <button
        type="button"
        aria-label="Clear search"
        onClick={() => onChange('')}
        className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
      >
        <X aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
    ) : null}
  </div>
);

export default SearchInput;
